import React, { Dispatch, SetStateAction, useMemo, useRef } from 'react';

import { FilterLabelTextForTracking, PerformanceType } from '@const/PerformanceType';

import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { PERFORMANCE_CARD_TITLE } from '@pages/Utilities';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { FactRevenue, PageName, PageType } from '@utils/pageConfig/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { IRootState } from '@state/reducers/Reducers';
import { getAppliedPgAndCountriesInfo } from '@utils/ProductGroupsAvailabilityUtils';
import { constructRevenueTitle } from '@utils/currencyUtils/CurrencyUtils';
import { WithHiddenListBox } from '@components/design';

type PerformanceFilterOption = { label: string; value: PerformanceType };

/* eslint-disable-next-line */
export interface PerformanceHeaderProps {
  selectedPerformanceFilter: string;
  changePerformanceFilter: Dispatch<SetStateAction<PerformanceType>>;
  getPerformanceCardResponse?: (type: PerformanceType) => void;
  volume: string;
  isLoading: boolean;
  factRevenue: FactRevenue;
}

const PerformanceFilter: React.FC<PerformanceHeaderProps> = ({
  selectedPerformanceFilter,
  changePerformanceFilter,
  getPerformanceCardResponse,
  volume,
  isLoading,
  factRevenue,
}) => {
  const dispPerfType = useRef(PerformanceType.Value);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const { totalAvailablePgs, totalSubscriptionPgs } = useSelector((state: IRootState) => state.availabilityPg);
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const handleClick = (value: string) => {
    //Start Chart Interaction Tracking
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.RETAILER_OVERVIEW,
      pageName: PageName.RETAILER_OVERVIEW,
      elementName: ChartElementName.PERFORMANCE,
      elementType: ChartElementType.FILTER,
      optionChosen: FilterLabelTextForTracking(volume)[value as PerformanceType],
      chartName: PERFORMANCE_CARD_TITLE,
      access: pagesConfig.access,
    };

    chartInteractionTrackingFunction(trackingParam);
    //End Chart Interaction Tracking
  };

  const onChange = (val: string) => {
    handleClick(val);
    if (dispPerfType.current !== val) {
      dispPerfType.current = val as PerformanceType;
      changePerformanceFilter(val as PerformanceType);
      if (getPerformanceCardResponse) getPerformanceCardResponse(val as PerformanceType);
    }
  };
  const options = [
    { label: `${constructRevenueTitle(factRevenue)} Performance`, value: PerformanceType.Value },
    { label: `${volume} Performance`, value: PerformanceType.Volume },
  ];

  const appliedFilterInfo = useMemo(() => {
    const appliedFilters = pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters;
    return getAppliedPgAndCountriesInfo(
      appliedFilters,
      totalAvailablePgs,
      totalSubscriptionPgs,
      PageName.RETAILER_OVERVIEW,
    );
  }, [totalAvailablePgs, totalSubscriptionPgs, pagesConfig]);

  return (
    <div className="flex mb-4" data-test-id="performance-filter">
      <WithHiddenListBox
        isDisabled={isLoading}
        options={options}
        label=""
        value={options.find((item: PerformanceFilterOption) => item.value === selectedPerformanceFilter)?.label}
        onChange={(option: PerformanceFilterOption) => onChange(option.value)}
      />
      {appliedFilterInfo && (
        <span className="mt-2" data-test-id="tiles-filter-summary">
          {appliedFilterInfo}
        </span>
      )}
    </div>
  );
};

export default PerformanceFilter;
